import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, useColorModeValue, Icon } from "@chakra-ui/react";
import { useCopyValue } from "hooks/useCopies";
import type { DealInputSuggestion, DealFilterAction } from "types/copy";
import * as MdIcons from "react-icons/md";
import { useConversationDialogSubmitFunctions } from "screens/thread/useConversationDialogSubmitFunctions";
import { useConversationContext } from "screens/thread/ConversationContext";

interface DealActionButtonsProps {
  size?: "sm" | "md";
}

export const DealActionButtons: React.FC<DealActionButtonsProps> = ({ size = "sm" }) => {
  const [activeButtonIndex, setActiveButtonIndex] = useState<number | null>(null);
  const bgColor = useColorModeValue("white", "gray.700");
  const activeColor = useColorModeValue("white", "gray.700");
  const activeBgColor = useColorModeValue("primary.default", "primary.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { onSubmitDealFinderQuery } = useConversationDialogSubmitFunctions({});
  const { setActiveFilter, activeFilter, setActiveFilterUrlKey, activeFilterUrlKey } = useConversationContext();

  const rawDealSuggestions = useCopyValue("copy_deal_input_suggestions", true) as (string | DealInputSuggestion)[] | undefined;
  const rawDealFilterActions = useCopyValue("copy_deal_filter_actions", true) as (string | DealFilterAction)[] | undefined;

  const dealActionButtons = useMemo(() => {
    const suggestions = rawDealSuggestions
      ? rawDealSuggestions
          .map((suggestion) => {
            if (typeof suggestion === "string") {
              try {
                return JSON.parse(suggestion) as DealInputSuggestion;
              } catch {
                return { key: suggestion, question: suggestion, label: suggestion, type: "quick_link" as const };
              }
            }
            return suggestion;
          })
          .filter((suggestion) => suggestion.type === "quick_link")
          .map((suggestion) => ({ ...suggestion, query: suggestion.question }))
      : [];

    const filterActions = rawDealFilterActions
      ? rawDealFilterActions
          .map((action) => {
            if (typeof action === "string") {
              try {
                return JSON.parse(action) as DealFilterAction;
              } catch {
                return null;
              }
            }
            return action;
          })
          .filter((action): action is DealFilterAction => action !== null)
          .map((action) => ({ ...action, query: action.url }))
      : [];

    return [...filterActions, ...suggestions];
  }, [rawDealSuggestions, rawDealFilterActions]);

  const isMdSize = size === "md";
  const filterButtonPx = isMdSize ? "10px" : "5px";
  const filterButtonPy = isMdSize ? "3px" : "1px";
  const filterButtonFontSize = isMdSize ? "13px" : "10px";
  const filterButtonBorderRadius = isMdSize ? "lg" : "md";

  const handleButtonClick = useCallback(
    (item: (DealInputSuggestion | DealFilterAction) & { query: string }, index: number) => {
      if (activeButtonIndex === index) {
        return;
      }
      setActiveButtonIndex(index);

      if ("url" in item) {
        setActiveFilterUrlKey(item.key);
        setActiveFilter(null);
      } else {
        onSubmitDealFinderQuery(item.query);
        setActiveFilterUrlKey(null);
      }
    },
    [activeButtonIndex, onSubmitDealFinderQuery, setActiveFilterUrlKey, setActiveFilter]
  );

  useEffect(() => {
    if (activeFilter === null && activeFilterUrlKey === null) {
      setActiveButtonIndex(null);
    }
  }, [activeFilter, activeFilterUrlKey]);

  const getIconComponent = (iconName?: string) => {
    if (!iconName) return null;
    const formattedIconName = iconName.startsWith("Md") ? iconName : `Md${iconName}`;

    const IconComponent = (MdIcons as any)[formattedIconName];
    return IconComponent || null;
  };

  if (dealActionButtons.length === 0) {
    return null;
  }

  return (
    <>
      {dealActionButtons.map((suggestion, index) => {
        const IconComponent = getIconComponent(suggestion.icon);
        const isActive = activeButtonIndex === index || ("url" in suggestion && activeFilterUrlKey === suggestion.url);
        const isDisabled = isActive;

        return (
          <Box
            key={"key" in suggestion ? suggestion.key : `deal-action-${index}`}
            className={`ch-filter-ticker-button ch-query-deal ch-filter-ticker-${isActive ? "active" : "inactive"}`}
            as="button"
            px={filterButtonPx}
            py={filterButtonPy}
            borderRadius={filterButtonBorderRadius}
            fontSize={filterButtonFontSize}
            whiteSpace="nowrap"
            fontWeight={isActive ? "semibold" : "normal"}
            bg={isActive ? activeBgColor : bgColor}
            color={isActive ? activeColor : "gray.600"}
            disabled={isDisabled}
            _hover={{
              color: isActive ? "gray.50" : "primary.default",
            }}
            _active={{}}
            border="1px solid"
            borderColor={borderColor}
            opacity={isDisabled ? 0.7 : 1}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick(suggestion, index);
            }}
            transition="all 0.2s"
            style={{ scrollSnapAlign: "start" }}
            display="flex"
            alignItems="center"
            gap="6px">
            {IconComponent && (
              <Icon
                as={IconComponent}
                boxSize={isMdSize ? "16px" : "14px"}
                color={isActive ? activeColor : suggestion.iconColor || "currentColor"}
              />
            )}
            {suggestion.label}
          </Box>
        );
      })}
    </>
  );
};
